// Practice Projects API Integration
// Date: January 21, 2026

import {
  QueryKey,
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import { toast } from 'sonner';
import getBITSEndApiMap from '../apiMap';
import { api } from '../services/AxiosService';
import type {
  AddQAItemPayload,
  CreatePracticeProjectPayload,
  CreatePracticeProjectResponse,
  DeletePracticeProjectResponse,
  DeleteQAItemResponse,
  EnrollPracticeProjectResponse,
  GetPracticeProjectsParams,
  GetPracticeProjectsResponse,
  MyEnrollmentsResponse,
  PracticeProjectDetailResponse,
  PracticeProjectStatsResponse,
  PublishPracticeProjectResponse,
  QAItemResponse,
  UnpublishPracticeProjectResponse,
  UpdatePracticeProjectPayload,
  UpdatePracticeProjectResponse,
  UpdateProgressResponse,
  UpdateQAItemPayload,
} from '../types/practiceProjects';

// ==================== API Functions ====================

/**
 * Get practice project statistics
 */
export async function getPracticeProjectStats(): Promise<PracticeProjectStatsResponse> {
  const url = getBITSEndApiMap('practiceProjectsStats');
  const { data } = await api.get<PracticeProjectStatsResponse>(url);
  return data;
}

/**
 * Get list of practice projects with filters and pagination
 */
export async function getPracticeProjects(
  params: GetPracticeProjectsParams = {}
): Promise<GetPracticeProjectsResponse> {
  const url = getBITSEndApiMap('getPracticeProjects');
  const { data } = await api.get<GetPracticeProjectsResponse>(url, { params });
  return data;
}

/**
 * Get practice project by ID with all associations
 */
export async function getPracticeProjectById(id: string): Promise<PracticeProjectDetailResponse> {
  const url = `${getBITSEndApiMap('getPracticeProjects')}/${id}`;
  const { data } = await api.get<PracticeProjectDetailResponse>(url);
  return data;
}

/**
 * Create a new practice project
 */
export async function createPracticeProject(
  payload: CreatePracticeProjectPayload | FormData
): Promise<CreatePracticeProjectResponse> {
  const url = getBITSEndApiMap('createPracticeProject');
  const config = payload instanceof FormData ? {
    headers: { 'Content-Type': 'multipart/form-data' }
  } : {};
  const { data } = await api.post<CreatePracticeProjectResponse>(url, payload, config);
  return data;
}

/**
 * Update an existing practice project
 */
export async function updatePracticeProject(
  id: string,
  payload: UpdatePracticeProjectPayload | FormData
): Promise<UpdatePracticeProjectResponse> {
  const url = `${getBITSEndApiMap('updatePracticeProject')}/${id}`;
  const config = payload instanceof FormData ? {
    headers: { 'Content-Type': 'multipart/form-data' }
  } : {};
  const { data } = await api.put<UpdatePracticeProjectResponse>(url, payload, config);
  return data;
}

/**
 * Soft delete a practice project
 */
export async function deletePracticeProject(id: string): Promise<DeletePracticeProjectResponse> {
  const url = `${getBITSEndApiMap('deletePracticeProject')}/${id}`;
  const { data } = await api.delete<DeletePracticeProjectResponse>(url);
  return data;
}

/**
 * Duplicate a practice project
 */
export async function duplicatePracticeProject(id: string, title?: string): Promise<CreatePracticeProjectResponse> {
  const url = `${getBITSEndApiMap('deletePracticeProject')}/${id}/duplicate`;
  const { data } = await api.post<CreatePracticeProjectResponse>(url, { title });
  return data;
}

/**
 * Publish a practice project
 */
export async function publishPracticeProject(id: string): Promise<PublishPracticeProjectResponse> {
  const url = `${getBITSEndApiMap('publishPracticeProject')}/${id}/publish`;
  const { data } = await api.patch<PublishPracticeProjectResponse>(url);
  return data;
}

/**
 * Unpublish a practice project
 */
export async function unpublishPracticeProject(id: string): Promise<UnpublishPracticeProjectResponse> {
  const url = `${getBITSEndApiMap('publishPracticeProject')}/${id}/unpublish`;
  const { data } = await api.patch<UnpublishPracticeProjectResponse>(url);
  return data;
}

/**
 * Enroll in a practice project
 */
export async function enrollInPracticeProject(id: string): Promise<EnrollPracticeProjectResponse> {
  const url = `${getBITSEndApiMap('enrollPracticeProject')}/${id}/enroll`;
  const { data } = await api.post<EnrollPracticeProjectResponse>(url);
  return data;
}
/**
 * Update last accessed time for a practice project enrollment
 */
export async function updateLastAccessedTime(id: string): Promise<{ isSuccess: boolean; message: string }> {
  const url = `${getBITSEndApiMap('enrollPracticeProject')}/${id}/access`;
  const { data } = await api.patch(url);
  return data;
}
/**
 * Update practice project progress
 */
export async function updatePracticeProjectProgress(
  id: string,
  payload: { completion_percentage?: number; status?: string }
): Promise<UpdateProgressResponse> {
  const url = `${getBITSEndApiMap('updatePracticeProjectProgress')}/${id}/progress`;
  const { data } = await api.patch<UpdateProgressResponse>(url, payload);
  return data;
}

/**
 * Get user's enrolled practice projects
 */
export async function getMyEnrollments(params: { search?: string } = {}): Promise<MyEnrollmentsResponse> {
  const url = getBITSEndApiMap('myPracticeProjectEnrollments');
  const { data } = await api.get<MyEnrollmentsResponse>(url, { params });
  return data;
}

// ==================== Q&A API Functions ====================

/**
 * Add a Q&A item to a practice project
 */
export async function addQAItem(
  projectId: string,
  payload: AddQAItemPayload
): Promise<QAItemResponse> {
  const url = `${getBITSEndApiMap('practiceProjectsQA')}/${projectId}/qa`;
  const { data } = await api.post<QAItemResponse>(url, payload);
  return data;
}

/**
 * Update a Q&A item
 */
export async function updateQAItem(
  projectId: string,
  qaId: string,
  payload: UpdateQAItemPayload
): Promise<QAItemResponse> {
  const url = `${getBITSEndApiMap('practiceProjectsQA')}/${projectId}/qa/${qaId}`;
  const { data } = await api.put<QAItemResponse>(url, payload);
  return data;
}

/**
 * Delete a Q&A item
 */
export async function deleteQAItem(
  projectId: string,
  qaId: string
): Promise<DeleteQAItemResponse> {
  const url = `${getBITSEndApiMap('practiceProjectsQA')}/${projectId}/qa/${qaId}`;
  const { data } = await api.delete<DeleteQAItemResponse>(url);
  return data;
}

// ==================== React Query Hooks ====================

/**
 * Hook to fetch practice project statistics
 */
export function usePracticeProjectStatsQuery(
  options: Omit<UseQueryOptions<PracticeProjectStatsResponse>, 'queryKey' | 'queryFn'> = {}
) {
  return useQuery({
    queryKey: ['practice-projects', 'stats'] as QueryKey,
    queryFn: getPracticeProjectStats,
    staleTime: 60_000, // 1 minute
    ...options,
  });
}

/**
 * Hook to fetch list of practice projects with filters
 */
export function usePracticeProjectsQuery(
  params: GetPracticeProjectsParams = {},
  options: Omit<UseQueryOptions<GetPracticeProjectsResponse>, 'queryKey' | 'queryFn'> = {}
) {
  return useQuery({
    queryKey: ['practice-projects', 'list', params] as QueryKey,
    queryFn: () => getPracticeProjects(params),
    staleTime: 30_000, // 30 seconds
    ...options,
  });
}

/**
 * Hook to fetch practice project details by ID
 */
export function usePracticeProjectQuery(
  id: string | undefined,
  options: Omit<UseQueryOptions<PracticeProjectDetailResponse>, 'queryKey' | 'queryFn'> = {}
) {
  return useQuery({
    queryKey: ['practice-projects', 'detail', id] as QueryKey,
    queryFn: () => getPracticeProjectById(id as string),
    enabled: !!id,
    staleTime: 30_000,
    ...options,
  });
}

/**
 * Hook to fetch user's enrolled practice projects
 */
export function useMyEnrollmentsQuery(
  params: { search?: string } = {},
  options: Omit<UseQueryOptions<MyEnrollmentsResponse>, 'queryKey' | 'queryFn'> = {}
) {
  return useQuery({
    queryKey: ['practice-projects', 'my-enrollments', params] as QueryKey,
    queryFn: () => getMyEnrollments(params),
    staleTime: 30_000,
    ...options,
  });
}

/**
 * Hook to create a new practice project
 */
export function useCreatePracticeProjectMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createPracticeProject,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['practice-projects'] });
      toast.success('Practice project created successfully!');
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to create practice project');
    },
  });
}

/**
 * Hook to update an existing practice project
 */
export function useUpdatePracticeProjectMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdatePracticeProjectPayload | FormData }) =>
      updatePracticeProject(id, payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['practice-projects'] });
      queryClient.invalidateQueries({ queryKey: ['practice-projects', 'detail', variables.id] });
      toast.success('Practice project updated successfully!');
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to update practice project');
    },
  });
}

/**
 * Hook to delete a practice project
 */
export function useDeletePracticeProjectMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deletePracticeProject,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['practice-projects'] });
      toast.success('Practice project deleted successfully!');
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to delete practice project');
    },
  });
}

/**
 * Hook to duplicate a practice project
 */
export function useDuplicatePracticeProjectMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => duplicatePracticeProject(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['practice-projects'] });
      toast.success('Practice project duplicated successfully!');
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to duplicate practice project');
    },
  });
}

/**
 * Hook to publish a practice project
 */
export function usePublishPracticeProjectMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: publishPracticeProject,
    onSuccess: (_, projectId) => {
      queryClient.invalidateQueries({ queryKey: ['practice-projects'] });
      queryClient.invalidateQueries({ queryKey: ['practice-projects', 'detail', projectId] });
      toast.success('Practice project published successfully!');
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to publish practice project');
    },
  });
}

/**
 * Hook to unpublish a practice project
 */
export function useUnpublishPracticeProjectMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: unpublishPracticeProject,
    onSuccess: (_, projectId) => {
      queryClient.invalidateQueries({ queryKey: ['practice-projects'] });
      queryClient.invalidateQueries({ queryKey: ['practice-projects', 'detail', projectId] });
      toast.success('Practice project unpublished successfully!');
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to unpublish practice project');
    },
  });
}

/**
 * Hook to enroll in a practice project
 */
export function useEnrollPracticeProjectMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: enrollInPracticeProject,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['practice-projects', 'my-enrollments'] });
      queryClient.invalidateQueries({ queryKey: ['practice-projects', 'list'] });
      queryClient.invalidateQueries({ queryKey: ['practice-projects', 'detail'] });
      toast.success('Enrolled successfully!');
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to enroll in practice project');
    },
  });
}



/**
 * Hook to update practice project progress
 */
export function useUpdateProgressMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, completion_percentage }: { id: string; completion_percentage: number }) =>
      updatePracticeProjectProgress(id, { completion_percentage }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['practice-projects', 'my-enrollments'] });
      toast.success('Progress updated!');
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to update progress');
    },
  });
}

/**
 * Hook to mark a practice project as completed
 */
export function useMarkAsCompletedMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      updatePracticeProjectProgress(id, { status: 'completed', completion_percentage: 100 }),
    onSuccess: (_, projectId) => {
      queryClient.invalidateQueries({ queryKey: ['practice-projects', 'my-enrollments'] });
      queryClient.invalidateQueries({ queryKey: ['practice-projects', 'detail', projectId] });
      toast.success('Project marked as completed!');
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to mark project as completed');
    },
  });
}

/**
 * Hook to update last accessed time for a practice project
 */
export function useUpdateLastAccessedMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateLastAccessedTime,
    onSuccess: () => {
      // Silently update the cache - no toast needed for this background operation
      queryClient.invalidateQueries({ queryKey: ['practice-projects', 'my-enrollments'] });
    },
    onError: () => {
      // Silently fail - this is not critical functionality
    },
  });
}

// ==================== Q&A Mutation Hooks ====================

/**
 * Hook to add a Q&A item to a practice project
 */
export function useAddQAItemMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, payload }: { projectId: string; payload: AddQAItemPayload }) =>
      addQAItem(projectId, payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['practice-projects', 'detail', variables.projectId] });
      toast.success('Q&A item added successfully!');
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to add Q&A item');
    },
  });
}

/**
 * Hook to update a Q&A item
 */
export function useUpdateQAItemMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, qaId, payload }: { projectId: string; qaId: string; payload: UpdateQAItemPayload }) =>
      updateQAItem(projectId, qaId, payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['practice-projects', 'detail', variables.projectId] });
      toast.success('Q&A item updated successfully!');
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to update Q&A item');
    },
  });
}

/**
 * Hook to delete a Q&A item
 */
export function useDeleteQAItemMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, qaId }: { projectId: string; qaId: string }) =>
      deleteQAItem(projectId, qaId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['practice-projects', 'detail', variables.projectId] });
      toast.success('Q&A item deleted successfully!');
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to delete Q&A item');
    },
  });
}

